import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { EQUIPMENT_TYPES } from '../../../equipment/utils/enums';

/**
 * Options for EQUIPMENT_INSPECTION: which equipment types are inspected,
 * and the checks answered for each device.
 */
export default function EquipmentInspectionOptions({ data, updateOptions }) {
  const { t } = useTranslation(['procedures', 'equipment']);
  const types = data.options?.equipment_types || [];
  const checks = data.options?.checks || [];

  const setCheckLabel = (index, label) => {
    updateOptions({ checks: checks.map((c, i) => (i === index ? { ...c, label } : c)) });
  };

  const removeCheck = (index) => {
    updateOptions({ checks: checks.filter((_, i) => i !== index) });
  };

  // Ids only need to be stable and unique within the step
  const addCheck = () => {
    updateOptions({ checks: [...checks, { id: `check_${Date.now().toString(36)}`, label: '' }] });
  };

  return (
    <Stack spacing={2}>
      <Autocomplete
        multiple
        options={EQUIPMENT_TYPES}
        value={types}
        onChange={(e, next) => updateOptions({ equipment_types: next })}
        getOptionLabel={(type) => t(`equipment:equipment_types.${type}`)}
        renderValue={(value, getItemProps) => value.map((type, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return <Chip key={key} size="small" label={t(`equipment:equipment_types.${type}`)} {...itemProps} />;
        })}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('procedures:equipment_inspection.options.types', 'Udstyrstyper')}
            helperText={t(
              'procedures:equipment_inspection.options.types_help',
              'Alt aktivt udstyr af disse typer får et kort i proceduren.',
            )}
          />
        )}
      />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('procedures:equipment_inspection.options.checks', 'Kontrolpunkter for hvert apparat')}
        </Typography>
        <Stack spacing={1}>
          {checks.map((check, index) => (
            <Box key={check.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 20 }}>{index + 1}.</Typography>
              <TextField
                size="small"
                fullWidth
                variant="standard"
                value={check.label}
                onChange={(e) => setCheckLabel(index, e.target.value)}
                placeholder={t('procedures:equipment_inspection.options.check_placeholder', 'Fx Er kabinettet intakt?')}
              />
              <IconButton size="small" onClick={() => removeCheck(index)} sx={{ color: 'text.secondary' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
        <Button size="small" startIcon={<AddIcon />} onClick={addCheck} sx={{ mt: 1 }}>
          {t('procedures:equipment_inspection.options.add_check', 'Tilføj kontrolpunkt')}
        </Button>
      </Box>
    </Stack>
  );
}
